/**
 * Offline cache for premium chapter notes + quizzes.
 *
 * - Stored in IndexedDB, keyed by userId (never in the shared service-worker cache), wiped on logout.
 * - Cache only: there is no export or download of this data.
 * - Cached chapters open only while the offline lease is valid. The lease is issued by the server.
 * - "Latest time seen" only moves forward, so setting the device clock back cannot extend a lease.
 * - Quiz progress is stored per question id, so it survives chapter updates.
 */
import type { AttemptPayload, ChapterOfflineBundle, OfflineLease } from "./chaptersApi";

const DB_NAME = "soorgaa-offline-chapters";
const DB_VERSION = 4;
const CHAPTERS = "chapters"; // key: `${userId}:${chapterId}:${version}`
const META = "meta"; // key: userId -> { leaseExpiresAt, maxTimeSeen }
const PROGRESS = "progress"; // key: `${userId}:${chapterId}` -> { answers: Record<questionId, optionId> }
const POSITIONS = "positions"; // key: `${userId}:${chapterId}` -> local notes position
const ASSETS = "assets"; // key: `${userId}:${chapterId}:${version}:${src}` -> Blob (figure images)
const ATTEMPTS = "attempts"; // key: `${userId}:${attemptId}` -> { payload, synced }

type Meta = { leaseExpiresAt: number | null; maxTimeSeen: number; leaseId?: string; signature?: string };

/** Tolerance for small clock drift before we treat the clock as set back. */
const CLOCK_TOLERANCE_MS = 2 * 60 * 1000;

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB unavailable"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      for (const name of [CHAPTERS, META, PROGRESS, POSITIONS, ATTEMPTS, ASSETS]) {
        if (!db.objectStoreNames.contains(name)) db.createObjectStore(name);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function run<T>(store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDb();
  try {
    return await new Promise<T>((resolve, reject) => {
      const tx = db.transaction(store, mode);
      const req = fn(tx.objectStore(store));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  } finally {
    db.close();
  }
}

/** The user id (`sub`) inside the access token, used only as a local cache key. */
export function userIdFromToken(token: string | null | undefined): string | null {
  if (!token) return null;
  try {
    const part = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const sub = JSON.parse(atob(part)).sub;
    return typeof sub === "string" ? sub : null;
  } catch {
    return null;
  }
}

async function getMeta(userId: string): Promise<Meta> {
  const meta = await run<Meta | undefined>(META, "readonly", (s) => s.get(userId));
  return meta ?? { leaseExpiresAt: null, maxTimeSeen: 0 };
}

async function putMeta(userId: string, meta: Meta): Promise<void> {
  await run(META, "readwrite", (s) => s.put(meta, userId));
}

/** Store a freshly issued lease and move the trusted time up to the server's time. */
export async function saveLease(userId: string, lease: OfflineLease): Promise<void> {
  const meta = await getMeta(userId);
  const serverNow = Date.parse(lease.server_time);
  await putMeta(userId, {
    leaseExpiresAt: Date.parse(lease.expires_at),
    leaseId: lease.lease_id,
    signature: lease.signature,
    maxTimeSeen: Math.max(meta.maxTimeSeen, Number.isFinite(serverNow) ? serverNow : 0),
  });
}

export type LeaseStatus = { valid: boolean; expiresAt: number | null; reason?: "none" | "expired" | "clock" };

/**
 * Check the lease whenever a cached chapter opens. If the device clock is earlier than the latest
 * server time we have seen, the lease is invalid until the next online check (deterrence, not DRM).
 */
export async function checkLease(userId: string): Promise<LeaseStatus> {
  const meta = await getMeta(userId);
  if (meta.leaseExpiresAt === null) return { valid: false, expiresAt: null, reason: "none" };
  const now = Date.now();
  if (now < meta.maxTimeSeen - CLOCK_TOLERANCE_MS) return { valid: false, expiresAt: meta.leaseExpiresAt, reason: "clock" };
  if (now > meta.maxTimeSeen) await putMeta(userId, { ...meta, maxTimeSeen: now });
  return now < meta.leaseExpiresAt
    ? { valid: true, expiresAt: meta.leaseExpiresAt }
    : { valid: false, expiresAt: meta.leaseExpiresAt, reason: "expired" };
}

export async function saveBundle(userId: string, bundle: ChapterOfflineBundle): Promise<void> {
  await saveLease(userId, bundle.lease);
  await run(CHAPTERS, "readwrite", (s) => s.put(bundle, `${userId}:${bundle.chapter_id}:${bundle.version}`));
  // Keep one version per chapter: drop older cached versions of this chapter.
  const all = await listBundles(userId);
  for (const b of all) {
    if (b.chapter_id === bundle.chapter_id && b.version < bundle.version) {
      await run(CHAPTERS, "readwrite", (s) => s.delete(`${userId}:${b.chapter_id}:${b.version}`));
      await deleteAssets(userId, b.chapter_id).catch(() => undefined); // images of the old version
    }
  }
}

/** Save figure images with the chapter (keyed by chapter_id + version). */
export async function saveAssets(userId: string, chapterId: string, version: number, files: Record<string, Blob>): Promise<void> {
  for (const [src, blob] of Object.entries(files)) {
    await run(ASSETS, "readwrite", (s) => s.put(blob, `${userId}:${chapterId}:${version}:${src}`));
  }
}

/** Object URLs for the saved images of a chapter version. Call URL.revokeObjectURL when done. */
export async function loadAssetUrls(userId: string, chapterId: string, version: number): Promise<Record<string, string>> {
  const prefix = `${userId}:${chapterId}:${version}:`;
  const range = IDBKeyRange.bound(prefix, `${prefix}\uffff`);
  const keys = await run<IDBValidKey[]>(ASSETS, "readonly", (s) => s.getAllKeys(range));
  const blobs = await run<Blob[]>(ASSETS, "readonly", (s) => s.getAll(range));
  const out: Record<string, string> = {};
  keys.forEach((k, i) => {
    out[String(k).slice(prefix.length)] = URL.createObjectURL(blobs[i]);
  });
  return out;
}

async function deleteAssets(userId: string, chapterId: string): Promise<void> {
  const prefix = `${userId}:${chapterId}:`;
  const range = IDBKeyRange.bound(prefix, `${prefix}\uffff`);
  const keys = await run<IDBValidKey[]>(ASSETS, "readonly", (s) => s.getAllKeys(range));
  for (const k of keys) await run(ASSETS, "readwrite", (s) => s.delete(k));
}

export async function listBundles(userId: string): Promise<ChapterOfflineBundle[]> {
  const range = IDBKeyRange.bound(`${userId}:`, `${userId}:￿`);
  return run<ChapterOfflineBundle[]>(CHAPTERS, "readonly", (s) => s.getAll(range));
}

export async function getBundle(userId: string, chapterId: string): Promise<ChapterOfflineBundle | null> {
  const all = await listBundles(userId);
  const mine = all.filter((b) => b.chapter_id === chapterId).sort((a, b) => b.version - a.version);
  return mine[0] ?? null;
}

export async function removeBundle(userId: string, chapterId: string): Promise<void> {
  await deleteAssets(userId, chapterId).catch(() => undefined);
  for (const b of await listBundles(userId)) {
    if (b.chapter_id === chapterId) {
      await run(CHAPTERS, "readwrite", (s) => s.delete(`${userId}:${b.chapter_id}:${b.version}`));
    }
  }
}

export async function saveProgress(userId: string, chapterId: string, answers: Record<string, string>): Promise<void> {
  await run(PROGRESS, "readwrite", (s) => s.put({ answers }, `${userId}:${chapterId}`));
}

export async function getProgress(userId: string, chapterId: string): Promise<Record<string, string>> {
  const row = await run<{ answers: Record<string, string> } | undefined>(PROGRESS, "readonly", (s) =>
    s.get(`${userId}:${chapterId}`),
  );
  return row?.answers ?? {};
}

/** Chapters with an unfinished quiz round on this device (answers saved per question id). */
export async function listUnfinishedQuizChapters(userId: string): Promise<string[]> {
  const range = IDBKeyRange.bound(`${userId}:`, `${userId}:￿`);
  const keys = await run<IDBValidKey[]>(PROGRESS, "readonly", (s) => s.getAllKeys(range));
  return keys.map((k) => String(k).slice(userId.length + 1));
}

export async function clearProgress(userId: string, chapterId: string): Promise<void> {
  await run(PROGRESS, "readwrite", (s) => s.delete(`${userId}:${chapterId}`));
}

export type LocalPosition = { chapter_version: number; topic_id: string; slide_id: string; client_updated_at: string };

export async function savePosition(userId: string, chapterId: string, pos: LocalPosition): Promise<void> {
  await run(POSITIONS, "readwrite", (s) => s.put(pos, `${userId}:${chapterId}`));
}

export async function getLocalPosition(userId: string, chapterId: string): Promise<LocalPosition | null> {
  return (await run<LocalPosition | undefined>(POSITIONS, "readonly", (s) => s.get(`${userId}:${chapterId}`))) ?? null;
}

type QueuedAttempt = { payload: AttemptPayload; synced: boolean };

/** Attempts are created on the device (scoring works offline) and synced when online. */
export async function queueAttempt(userId: string, payload: AttemptPayload): Promise<void> {
  const row: QueuedAttempt = { payload, synced: false };
  await run(ATTEMPTS, "readwrite", (s) => s.put(row, `${userId}:${payload.attempt_id}`));
}

export async function pendingAttempts(userId: string): Promise<AttemptPayload[]> {
  const range = IDBKeyRange.bound(`${userId}:`, `${userId}:￿`);
  const rows = await run<QueuedAttempt[]>(ATTEMPTS, "readonly", (s) => s.getAll(range));
  return rows.filter((r) => !r.synced).map((r) => r.payload);
}

export async function markAttemptSynced(userId: string, attemptId: string): Promise<void> {
  const key = `${userId}:${attemptId}`;
  const row = await run<QueuedAttempt | undefined>(ATTEMPTS, "readonly", (s) => s.get(key));
  if (row) await run(ATTEMPTS, "readwrite", (s) => s.put({ ...row, synced: true }, key));
}

/** Send queued attempts. The server is idempotent on attempt_id, so retrying is safe. */
export async function syncAttempts(userId: string, send: (a: AttemptPayload) => Promise<unknown>): Promise<number> {
  let sent = 0;
  for (const a of await pendingAttempts(userId)) {
    try {
      await send(a);
      await markAttemptSynced(userId, a.attempt_id);
      sent += 1;
    } catch {
      break; // still offline or server busy: keep the rest queued
    }
  }
  return sent;
}

/** Remove everything on this device (called on logout). */
export function wipeAllOfflineChapters(): void {
  try {
    if (typeof indexedDB !== "undefined") indexedDB.deleteDatabase(DB_NAME);
  } catch {
    /* nothing to wipe */
  }
}
